// Module registry that drives the client nav + routes.
//
// Seeded at scaffold time (a snapshot of the backend modules.config.js) so the
// SPA has an instant, offline-capable first paint — then reconciled at runtime
// from GET /api/meta/modules so edits to modules.config.js show up WITHOUT a
// client rebuild. The runtime copy is the single source of truth; the seed is
// only the first-paint fallback. Consume via useModules().
// It equals modules.config.js minus the capability modules (D57: never shown before the live
// copy says the store holds them); src/merchant/__tests__/modulesParity.test.js keeps it so.
import { useState, useEffect } from 'react';
import axios from 'axios';

export const seedModules = {
  "core": {
    "key": "core",
    "label": "Core",
    "icon": "layout-dashboard",
    "models": [
      "User",
      "Role",
      "Permission",
      "ApiToken",
      "StaffInvite"
    ],
    "routes": [
      "/",
      "/users",
      "/roles",
      "/permissions",
      "/api-tokens",
      "/settings",
      "/profile"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "manage_tokens"
    ],
    "instanceScoped": []
  },
  "inventory": {
    "key": "inventory",
    "label": "Inventory",
    "icon": "package",
    "models": [
      "InventoryUnit",
      "Category"
    ],
    "routes": [
      "/inventory-unit",
      "/category"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "inspect",
      "import",
      "condition_rules"
    ],
    "instanceScoped": []
  },
  "catalog": {
    "key": "catalog",
    "label": "Catalog",
    "icon": "laptop",
    "models": [
      "ProductModel",
      "Listing",
      "ListingVariant",
      "Brand",
      "SizeChart"
    ],
    "routes": [
      "/product-model",
      "/listing",
      "/brand"
    ],
    "actions": [
      "view",
      "create",
      "update",
      "delete",
      "publish",
      "submit"
    ],
    "instanceScoped": []
  },
  "sales": {
    "key": "sales",
    "label": "Sales",
    "icon": "receipt",
    "models": [
      "Customer",
      "SalesOrder",
      "Checkout",
      "Refund",
      "Lead",
      "Quotation",
      "Coupon",
      "StorefrontEvent"
    ],
    "routes": [],
    "actions": [
      "view",
      "create",
      "update",
      "accept",
      "pick",
      "pack",
      "dispatch",
      "deliver",
      "cancel",
      "refund",
      "return",
      "exception"
    ],
    "instanceScoped": [],
    "crud": false
  },
  "service": {
    "key": "service",
    "label": "Service",
    "icon": "wrench",
    "feature": "service",
    "models": [
      "ServiceTicket",
      "ServiceJob",
      "ServiceEstimate",
      "Part",
      "Technician",
      "FieldVisit"
    ],
    "routes": [],
    "actions": [
      "view",
      "create",
      "update",
      "triage",
      "diagnose",
      "quote",
      "repair",
      "qc",
      "close",
      "assign",
      "field",
      "custody",
      "invoice"
    ],
    "instanceScoped": [],
    "crud": false
  },
  "buyback": {
    "key": "buyback",
    "label": "Buy-back",
    "icon": "wallet",
    "feature": "buyback",
    "models": [
      "BuybackEstimate",
      "BuybackBooking"
    ],
    "routes": [],
    "actions": [
      "view"
    ],
    "instanceScoped": [],
    "crud": false
  },
  "warranty": {
    "key": "warranty",
    "label": "Warranty",
    "icon": "shield",
    "models": [
      "Warranty",
      "WarrantyClaim"
    ],
    "routes": [],
    "actions": [
      "view",
      "claim"
    ],
    "instanceScoped": [],
    "crud": false
  },
  "finance": {
    "key": "finance",
    "label": "Finance",
    "icon": "wallet",
    "models": [
      "Invoice",
      "LedgerTransaction",
      "SettlementLine",
      "Payout"
    ],
    "routes": [],
    "actions": [
      "view",
      "view_cost",
      "payout"
    ],
    "instanceScoped": [],
    "crud": false
  },
  "store": {
    "key": "store",
    "label": "Store setup",
    "icon": "store",
    "models": [
      "Location",
      "PinZone",
      "StorePolicy",
      "StorefrontPage",
      "StorefrontTemplate"
    ],
    "routes": [],
    "actions": [
      "view",
      "update",
      "domains",
      "policies",
      "appearance",
      "custom_code",
      "features",
      "email"
    ],
    "instanceScoped": [],
    "crud": false
  },
  "marketplace": {
    "key": "marketplace",
    "label": "Vendors",
    "icon": "users",
    "models": [
      "StoreVendor"
    ],
    "routes": [],
    "actions": [
      "view",
      "create",
      "update",
      "approve"
    ],
    "instanceScoped": [],
    "crud": false
  }
};
export default seedModules;

// Shared across hook instances: once the live registry arrives, later mounts
// (and a second consumer on the same page) start from the fresh copy.
let cache = seedModules;

let live = false;

/**
 * Live module registry and whether it has arrived (`ready`). The seed lacks the
 * capability modules (supply, dropship, D57), so a screen of one is routed only
 * once the live copy is in; until then the app must not treat its path as 404.
 */
export function useModuleRegistry() {
  const [state, setState] = useState({ modules: cache, ready: live });
  useEffect(() => {
    let active = true;
    axios
      .get('/api/meta/modules', { withCredentials: true })
      .then((res) => {
        if (active && res.data && typeof res.data === 'object') {
          cache = res.data;
          live = true;
          setState({ modules: res.data, ready: true });
        }
      })
      .catch(() => {
        /* offline / not yet reachable — keep the seeded copy */
        if (active) setState((s) => ({ ...s, ready: true }));
      });
    return () => {
      active = false;
    };
  }, []);
  return state;
}

/** Live module registry — seeded from the build-time copy, reconciled from the API. */
export function useModules() {
  return useModuleRegistry().modules;
}
